import { formatCurrency } from "@components/scope-of-work/components/ListTable/components/helpers";
import { Box, Button, Typography } from "@mui/material";
import { memo, useMemo, useState } from "react";
import { createPortingCheckout, fetchPortingStatus, requestIncludedNumber } from "../../PaySetupFee/flow-mobile-stripe.actions";
import { ICreateSetupFeeOrderPayload, ISetupFeePaymentSuccessData } from "../../PaySetupFee/pay.setup.fee.interfaces";
import { PortingStripeCheckoutDialog } from "./PortingStripeCheckoutDialog";

export interface SetupFeeBarProps {
  isPorting: boolean;
  setupFee?: number;
  isLoading?: boolean;
  disabled?: boolean;
  setupPayload: ICreateSetupFeeOrderPayload | null;
  onPaymentSuccess?: (data: ISetupFeePaymentSuccessData) => void;
  onPaymentError?: (error: unknown) => void;
  onPaymentPending?: (message: string) => void;
  onContinue?: () => void;
}

const ORANGE = "#FB8E00";

function SetupFeeBarComponent({
  isPorting,
  setupFee = 9.99,
  isLoading = false,
  disabled = false,
  setupPayload,
  onPaymentSuccess,
  onPaymentError,
  onPaymentPending,
}: SetupFeeBarProps) {
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState<{ invoiceUid: string; clientSecret: string } | null>(null);
  const formattedFee = useMemo(() => {
    return formatCurrency(setupFee);
  }, [setupFee]);

  const actionDisabled = useMemo(() => {
    return disabled || isLoading || submitting || !setupPayload;
  }, [disabled, isLoading, submitting, setupPayload]);

  const description = isPorting
    ? "Portability stays a one-time charge. The request stays blocked until the payment is confirmed."
    : "A new number is included in your Flow Mobile subscription. It is activated only after that payment is confirmed.";

  const handleContinue = async () => {
    if (!setupPayload || actionDisabled) return;
    setSubmitting(true);
    try {
      if (isPorting) {
        const created = await createPortingCheckout(setupPayload);
        if (!created.clientSecret) {
          throw new Error("Stripe did not return an embedded checkout session");
        }
        setCheckout({ invoiceUid: created.invoiceUid, clientSecret: created.clientSecret });
        return;
      }

      const result = await requestIncludedNumber(setupPayload);
      if (result.status === "provisioned") {
        onPaymentSuccess?.({
          orderId: result.subscriptionId,
          invoiceUid: "",
          captureId: null,
          captureStatus: "provisioned",
          paidAt: new Date().toISOString(),
          grossAmount: 0,
          fee: null,
          netAmount: null,
        });
        return;
      }

      sessionStorage.setItem("flowMobileBuyingSubscription", result.subscriptionId);
      onPaymentPending?.("Waiting for the Flow Mobile subscription payment to be confirmed. The number stays pending until then.");
    } catch (error) {
      onPaymentError?.(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckoutComplete = () => {
    const invoiceUid = checkout?.invoiceUid;
    setCheckout(null);
    if (invoiceUid) {
      sessionStorage.setItem("flowMobilePortingInvoice", invoiceUid);
    }
    onPaymentPending?.("Waiting for the porting payment to be confirmed. This request stays blocked until then.");
  };

  const handleCheckoutClose = async () => {
    const invoiceUid = checkout?.invoiceUid;
    setCheckout(null);
    if (!invoiceUid) return;

    try {
      const status = await fetchPortingStatus(invoiceUid);
      if (status.status === "paid" && status.provisioned) {
        sessionStorage.removeItem("flowMobilePortingInvoice");
        onPaymentSuccess?.({
          orderId: invoiceUid,
          invoiceUid,
          captureId: null,
          captureStatus: "provisioned",
          paidAt: status.paidAt || new Date().toISOString(),
          grossAmount: setupFee,
          fee: null,
          netAmount: null,
        });
        return;
      }
      if (status.status === "paid") {
        onPaymentPending?.("Waiting for the porting payment to be confirmed. This request stays blocked until then.");
        return;
      }
    } catch (error) {
      console.error("Flow Mobile porting status check failed:", error);
    }

    sessionStorage.removeItem("flowMobilePortingInvoice");
  };

  return (
    <>
      <PortingStripeCheckoutDialog
        open={Boolean(checkout)}
        clientSecret={checkout?.clientSecret ?? null}
        onClose={handleCheckoutClose}
        onComplete={handleCheckoutComplete}
      />
      <Box
        sx={{
          mt: 2.5,
          borderRadius: "18px",
          border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.75, sm: 2.25 },
            py: { xs: 1.75, sm: 3 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.65,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: ORANGE,
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "var(--fx-surface-text, #0F172A)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  lineHeight: 1,
                }}
              >
                Phone setup payment
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1.4,
                maxWidth: 650,
              }}
            >
              {description}
            </Typography>

            <Typography
              sx={{
                mt: 0.45,
                fontSize: 12,
                fontWeight: 500,
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1.35,
              }}
            >
              {isPorting
                ? "Your plan is already active. This charge is only the portability fee."
                : "No extra setup fee. The first number is included in the monthly subscription."}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "flex-end",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                textAlign: { xs: "left", sm: "right" },
                minWidth: { sm: 110 },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  lineHeight: 1,
                }}
              >
                {isPorting ? "Due now" : "Included"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.45,
                  fontSize: 28,
                  fontWeight: 950,
                  color: "var(--fx-surface-text, #0F172A)",
                  letterSpacing: "-0.05em",
                  lineHeight: 1,
                }}
              >
                {isPorting ? formattedFee : "Subscription"}
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: { xs: "100%", sm: 260 },
              }}
            >
              <Button
                fullWidth
                variant="contained"
                disabled={actionDisabled}
                onClick={handleContinue}
                sx={{ bgcolor: "#FB8E00", fontWeight: 800, textTransform: "none", "&:hover": { bgcolor: "#E07E00" } }}
              >
                {submitting ? "Opening payment..." : isPorting ? "Pay for porting" : "Activate included number"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export const SetupFeeBar = memo(SetupFeeBarComponent);
